import React, { useCallback, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import { CalendarClock, FileSignature, Info, ListChecks, Users, X } from 'lucide-react';
import { useNotifications } from 'src/notifications';

import type { IProperty } from 'src/services/api';
import Select from 'src/components/input/select/Select';
import { usePropertyForm } from 'src/components/create-or-update-property-modal/use-property-form';
import PropertyForm from 'src/components/create-or-update-property-modal/PropertyForm';
import PropertyImageEditor from 'src/components/create-or-update-property-modal/PropertyImageEditor';
import PropertyFactsSection from 'src/components/create-or-update-property-modal/PropertyFactsSection';
import PropertyContactsSection from 'src/components/create-or-update-property-modal/PropertyContactsSection';
import PropertyTourSettingsSection from 'src/components/create-or-update-property-modal/PropertyTourSettingsSection';
import PropertyApplicationLinksSection from 'src/components/create-or-update-property-modal/PropertyApplicationLinksSection';
import {
    type PropertySectionKey,
    usePropertySectionNavigation,
} from 'src/components/create-or-update-property-modal/use-property-section-navigation';

import styles from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal.module.css';

type Props = {
    title?: string;
    property?: IProperty;
    defaultValues?: Partial<IProperty>;
    opened: boolean;
    onClose: () => void;
    onSave?: (property: IProperty) => Promise<void> | void;
    initialSection?: PropertySectionKey;
    /**
     * Element id inside Tour Settings to open scrolled to (e.g. the contacts or
     * tour-availability subsection, which have no nav entry of their own). Takes
     * precedence over the section-level initial scroll.
     */
    scrollToAnchorId?: string;
};

const SECTION_LABELS: Record<PropertySectionKey, string> = {
    basicInfo: 'Basic Info',
    propertyFacts: 'Property Facts',
    contacts: 'Contacts',
    tourSettings: 'Tour Settings',
    applicationLinks: 'Application Settings',
};

const SECTION_ICONS: Record<PropertySectionKey, React.ReactNode> = {
    basicInfo: <Info size={15} />,
    propertyFacts: <ListChecks size={15} />,
    contacts: <Users size={15} />,
    tourSettings: <CalendarClock size={15} />,
    applicationLinks: <FileSignature size={15} />,
};

/**
 * Only Basic Info is driven by the shared react-hook-form instance. The other
 * sections save themselves, so its nav item is the only one that shows a
 * dirty-state dot.
 */
const FORM_BACKED_SECTION: PropertySectionKey = 'basicInfo';

export default function CreateOrUpdatePropertyModal({
    title: propsTitle,
    property,
    defaultValues,
    opened,
    onClose,
    onSave,
    initialSection = 'basicInfo',
    scrollToAnchorId,
}: Props) {

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [isConfirmingDiscard, setConfirmingDiscard] = useState(false);
    const { addNotification } = useNotifications();

    const onSuccess = (_property: IProperty) => {
        setImageFile(null);
        onSave?.(_property);
        if (!property) {
            onClose();
        }
    };

    const { form, submit } = usePropertyForm({
        onSuccess,
        property,
        defaultValues,
        imageFile,
    });
    const isEditing = !!property;
    const title = propsTitle || (isEditing ? 'Edit Property' : 'Create Property');

    const visibleSections = useMemo<PropertySectionKey[]>(() => {
        const sections: PropertySectionKey[] = ['basicInfo'];
        if (property?.facts !== undefined) {
            sections.push('propertyFacts');
        }
        if (property) {
            sections.push('contacts', 'tourSettings', 'applicationLinks');
        }
        return sections;
    }, [property]);

    const {
        activeSection,
        contentRef,
        handleContentScroll,
        scrollToSection,
        scrollToAnchor,
        setSectionRef,
    } = usePropertySectionNavigation(visibleSections, initialSection);

    /**
     * The dialog's scroll geometry is only settled once the MUI open transition
     * finishes, so the initial scroll is driven off the transition's onEntered
     * rather than a fixed delay. An explicit anchor id (a subsection inside Tour
     * Settings with no nav entry) takes precedence over the section-level scroll.
     */
    const handleEntered = useCallback(() => {
        if (scrollToAnchorId != null) {
            scrollToAnchor(scrollToAnchorId);
        } else if (visibleSections.includes(initialSection)) {
            scrollToSection(initialSection);
        }
    }, [scrollToAnchorId, scrollToAnchor, visibleSections, initialSection, scrollToSection]);

    const handleSave = useCallback(
        (event?: React.BaseSyntheticEvent) => form.handleSubmit(
            submit,
            () => addNotification('Fix the highlighted property fields before saving.', 'error')
        )(event),
        [addNotification, form, submit]
    );

    const { isDirty, isSubmitting } = form.formState;
    const hasUnsavedChanges = isDirty || !!imageFile;

    /**
     * Closing with unsaved edits silently threw the work away before, which is
     * easy to trigger with a stray backdrop click on a long form.
     */
    const handleRequestClose = useCallback(() => {
        if (hasUnsavedChanges) {
            setConfirmingDiscard(true);
            return;
        }
        onClose();
    }, [hasUnsavedChanges, onClose]);

    const handleDiscard = useCallback(() => {
        setConfirmingDiscard(false);
        setImageFile(null);
        form.reset();
        onClose();
    }, [form, onClose]);

    const sectionOptions = useMemo(
        () => visibleSections.map((key) => ({
            value: key,
            label: <Typography component="span" variant="body2">{SECTION_LABELS[key]}</Typography>,
        })),
        [visibleSections]
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog
                open={opened}
                onClose={handleRequestClose}
                slotProps={{
                    transition: { onEntered: handleEntered },
                    paper: {
                        className: styles.paper,
                    },
                }}
            >
                <DialogTitle className={styles.dialogTitle}>
                    <span className={styles.titleText}>
                        <span className={styles.titleEyebrow}>{title}</span>
                        {isEditing ? (
                            <span className={styles.titleName} title={property.name}>{property.name}</span>
                        ) : (
                            <span className={styles.titleName}>New property</span>
                        )}
                    </span>
                    <IconButton aria-label="Close property editor" onClick={handleRequestClose} data-testid="close-property-editor-button">
                        <X size={20} />
                    </IconButton>
                </DialogTitle>
                <div className={styles.header}>
                    <PropertyImageEditor
                        form={form}
                        currentImageUrl={property?.image_url}
                        selectedFile={imageFile}
                        onSelectFile={setImageFile}
                    />
                </div>
                {visibleSections.length > 1 ? (
                    <div className={styles.mobileNav}>
                        <Select
                            label="Section"
                            options={sectionOptions}
                            value={activeSection}
                            onChange={scrollToSection}
                            size="small"
                            fullWidth
                            data-testid="property-section-mobile-select"
                        />
                    </div>
                ) : null}
                <div className={styles.body}>
                    {visibleSections.length > 1 ? (
                        <nav className={styles.sidebar} aria-label="Property sections">
                            {visibleSections.map((key) => (
                                <button
                                    key={key}
                                    type="button"
                                    aria-current={key === activeSection}
                                    className={key === activeSection ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem}
                                    onClick={() => scrollToSection(key)}
                                    data-testid={`property-section-nav-${key}`}
                                >
                                    <span className={styles.navIcon}>{SECTION_ICONS[key]}</span>
                                    <span className={styles.navLabel}>{SECTION_LABELS[key]}</span>
                                    {key === FORM_BACKED_SECTION && hasUnsavedChanges ? (
                                        <span className={styles.navDot} aria-label="Unsaved changes" />
                                    ) : null}
                                </button>
                            ))}
                        </nav>
                    ) : null}
                    <DialogContent
                        ref={contentRef}
                        className={styles.content}
                        onScroll={handleContentScroll}
                    >
                        <section
                            ref={setSectionRef('basicInfo')}
                            className={styles.section}
                        >
                            <div className={styles.basicInfoHeader}>
                                {/* redundant when it is the only section and the rail is hidden */}
                                {visibleSections.length > 1 ? (
                                    <Typography className={styles.sectionTitle} data-property-section-title>Basic Info</Typography>
                                ) : null}
                                {hasUnsavedChanges && (
                                    <Typography
                                        variant="caption"
                                        className={styles.basicInfoDirtyIndicator}
                                        data-testid="property-editor-status"
                                    >
                                        Unsaved changes
                                    </Typography>
                                )}
                            </div>
                            <PropertyForm
                                form={form}
                                onSubmit={handleSave}
                            />
                            <div className={styles.basicInfoActions}>
                                <LoadingButton
                                    className={styles.sectionSaveButton}
                                    variant="contained"
                                    size="small"
                                    loading={isSubmitting}
                                    // on create, stay clickable so submitting an untouched form surfaces
                                    // the required-field errors rather than doing nothing
                                    disabled={isEditing && !hasUnsavedChanges}
                                    onClick={handleSave}
                                    data-testid="save-basic-info-button"
                                >
                                    {isEditing ? 'Save changes' : 'Create property'}
                                </LoadingButton>
                            </div>
                        </section>

                        {property?.facts !== undefined ? (
                            <section
                                ref={setSectionRef('propertyFacts')}
                                className={styles.section}
                            >
                                <PropertyFactsSection property={property} />
                            </section>
                        ) : null}

                        {property ? (
                            <>
                                <section
                                    ref={setSectionRef('contacts')}
                                    className={styles.section}
                                >
                                    <PropertyContactsSection property={property} />
                                </section>

                                <section
                                    ref={setSectionRef('tourSettings')}
                                    className={styles.section}
                                >
                                    <PropertyTourSettingsSection property={property} />
                                </section>

                                <section
                                    ref={setSectionRef('applicationLinks')}
                                    className={styles.section}
                                >
                                    <PropertyApplicationLinksSection property={property} />
                                </section>
                            </>
                        ) : null}
                        {visibleSections.length > 1 ? (
                            <div className={styles.sectionScrollSpacer} aria-hidden="true" />
                        ) : null}
                    </DialogContent>
                </div>
            </Dialog>

            <Dialog open={isConfirmingDiscard} onClose={() => setConfirmingDiscard(false)}>
                <DialogTitle>
                    <Typography variant="h3" component="span">Discard changes?</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This property has edits that have not been saved. Closing now discards them.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setConfirmingDiscard(false)}>
                        Keep editing
                    </Button>
                    <Button variant="contained" color="error" onClick={handleDiscard} data-testid="discard-property-changes-button">
                        Discard
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
}
